import { useMemo, useState } from "react";
import styled from "styled-components";
import { FaCheck } from "react-icons/fa6";
import Overlay from "../../../Componentes/Overlay";
import FormComp from "../../../Componentes/FormComp";
import Modal from "../../../Componentes/Utils/Modal";
import Formulario from "../../../Componentes/Utils/Formulario";
import MultiFileUploader from "../../../Componentes/Utils/MultiFileUploader";
import { formatCurrency } from "../../../Funcoes/formatCurrency";
import { formatBRLFromCents, parseCentsFromInput } from "../../../Funcoes/Money";
import { useModal } from "../../../contexts/ModalContext";
import { useToast } from "../../../contexts/ToastContext";
import useApi from "../../../hooks/useApi";

const Title = styled.h3`
    margin: 0 0 8px 0;
    font-size: 18px;
    color: #263b4b;
`;

const Subtitle = styled.p`
    margin: 0 0 16px 0;
    font-size: 14px;
    color: #555;
`;

const InfoRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 14px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f3f4f6;
`;

const InfoLabel = styled.span`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #6b7280;
    text-transform: uppercase;
`;

const InfoValue = styled.span`
    font-size: 14px;
    color: #111827;
    word-break: break-word;
`;

const Hint = styled.p`
    margin: 8px 0 0 0;
    font-size: 13px;
    color: ${({ $ok }) => ($ok ? "#15803d" : "#b45309")};
    display: flex;
    align-items: center;
    gap: 6px;
`;

const SectionLabel = styled.p`
    margin: 16px 0 8px 0;
    font-size: 12px;
    font-weight: 700;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.03em;
`;

const AnexosExistentes = styled.ul`
    list-style: none;
    margin: 0 0 12px 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const AnexoItem = styled.li`
    a {
        color: #1d4ed8;
        font-size: 13px;
        text-decoration: none;
        word-break: break-all;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const BotaoSalvar = styled.button`
    margin-top: 18px;
    width: 100%;
    height: 42px;
    border: none;
    border-radius: 10px;
    background-color: #4caf50;
    color: #fff;
    font-weight: bold;
    font-size: 13px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover:not(:disabled) {
        background-color: #45a049;
    }

    &:disabled {
        background-color: #c1c1c1;
        cursor: not-allowed;
    }
`;

const LogTabela = styled.table`
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
    margin-top: 4px;

    th {
        text-align: left;
        padding: 8px 12px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #64748b;
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
    }

    td {
        padding: 8px 12px;
        font-size: 13px;
        color: #1e293b;
        border-top: 1px solid #f1f5f9;
    }

    td.valor {
        text-align: right;
        white-space: nowrap;
    }
`;

const LogVazio = styled.p`
    margin: 4px 0 0 0;
    font-size: 13px;
    color: #64748b;
`;

const LogScroll = styled.div`
    max-height: 220px;
    overflow-y: auto;
`;

function formatMoney(value) {
    if (value == null || Number.isNaN(Number(value))) return "-";
    return `R$ ${formatCurrency(value)}`;
}

function formatDateTime(value) {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("pt-BR");
}

function toCents(value) {
    return Math.round(Number(value) * 100);
}

const HistoricoLog = ({ logs }) => {
    if (!logs || logs.length === 0) {
        return <LogVazio>Nenhum registro de alteração ainda.</LogVazio>;
    }

    return (
        <LogScroll>
            <LogTabela>
                <thead>
                    <tr>
                        <th>Usuário</th>
                        <th>Data</th>
                        <th>Valor</th>
                    </tr>
                </thead>
                <tbody>
                    {logs.map((log) => (
                        <tr key={log.id}>
                            <td>{log.usuario || "-"}</td>
                            <td>{formatDateTime(log.created_at)}</td>
                            <td className="valor">{formatMoney(log.valor_conciliado)}</td>
                        </tr>
                    ))}
                </tbody>
            </LogTabela>
        </LogScroll>
    );
};

const ModalConciliacaoConta = ({ row, periodo, onSaved, readOnly = false }) => {
    const api = useApi();
    const { closeModal } = useModal();
    const { notifyError, notifySuccess } = useToast();

    const saldo = Number(row?.saldo ?? 0);
    const saldoCents = toCents(saldo);

    const [valorCents, setValorCents] = useState(() => {
        if (row?.conciliacao?.valor_conciliado != null) {
            return toCents(row.conciliacao.valor_conciliado);
        }
        return 0;
    });
    const [anexos, setAnexos] = useState([]);
    const [saving, setSaving] = useState(false);

    const valoresBatem = useMemo(
        () => valorCents === saldoCents,
        [valorCents, saldoCents]
    );

    const anexosExistentes = row?.conciliacao?.anexos ?? [];
    const logs = row?.conciliacao?.logs ?? [];
    const preenchidoPor = row?.conciliacao?.preenchido_por;
    const preenchidoEm = row?.conciliacao?.preenchido_em;

    const handleValorChange = (e) => {
        setValorCents(parseCentsFromInput(e.target.value));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (readOnly || saving || !valoresBatem || !periodo || !row?.conta) return;

        setSaving(true);

        const formData = new FormData();
        formData.append("conta", row.conta);
        formData.append("ano", String(periodo.ano));
        formData.append("mes", String(Number(periodo.mes)));
        formData.append("valor_conciliado", (valorCents / 100).toFixed(2));

        anexos.forEach((item) => {
            if (item?.file) formData.append("files[]", item.file);
        });

        try {
            const { data } = await api.post(
                "/Painel/API/Contabilidade/Conciliacao/Save/index.php",
                formData
            );

            if (data?.success) {
                notifySuccess(data?.message || "Conciliação salva.");
                onSaved?.();
                closeModal();
                return;
            }

            notifyError(data?.message || "Erro ao salvar conciliação.");
        } catch (err) {
            console.error(err);
            notifyError(err?.response?.data?.message || "Erro ao salvar conciliação.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Overlay state changeState={closeModal}>
            <Modal header close={closeModal} size="lg">
                <Title>{readOnly ? "Detalhes da conciliação" : "Conciliar conta"}</Title>
                <Subtitle>
                    {readOnly
                        ? "Você não é responsável por esta conta. Visualização somente leitura."
                        : "Informe o valor conciliado. O botão Salvar só habilita quando o valor for igual ao saldo."}
                </Subtitle>

                <InfoRow>
                    <InfoLabel>Conta</InfoLabel>
                    <InfoValue>
                        {row?.conta} — {row?.descricao || "-"}
                    </InfoValue>
                </InfoRow>

                <InfoRow>
                    <InfoLabel>Saldo (TOTVS)</InfoLabel>
                    <InfoValue>{formatMoney(saldo)}</InfoValue>
                </InfoRow>

                {row?.conciliacao && (
                    <InfoRow>
                        <InfoLabel>Preenchido por</InfoLabel>
                        <InfoValue>
                            {preenchidoPor || "-"}
                            {preenchidoEm ? ` em ${formatDateTime(preenchidoEm)}` : ""}
                        </InfoValue>
                    </InfoRow>
                )}

                {readOnly ? (
                    <>
                        <InfoRow>
                            <InfoLabel>Valor conciliado</InfoLabel>
                            <InfoValue>
                                {row?.conciliacao
                                    ? formatMoney(row.conciliacao.valor_conciliado)
                                    : "Sem conciliação registrada"}
                            </InfoValue>
                        </InfoRow>

                        {anexosExistentes.length > 0 && (
                            <>
                                <SectionLabel>Anexos</SectionLabel>
                                <AnexosExistentes>
                                    {anexosExistentes.map((anexo) => (
                                        <AnexoItem key={anexo.id}>
                                            <a
                                                href={anexo.caminho}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {anexo.nome_original || "Arquivo"}
                                            </a>
                                        </AnexoItem>
                                    ))}
                                </AnexosExistentes>
                            </>
                        )}
                    </>
                ) : (
                    <Formulario onSubmit={handleSubmit}>
                        <FormComp
                            title="VALOR CONCILIADO"
                            type="text"
                            inputMode="numeric"
                            placeholder="R$ 0,00"
                            valor={formatBRLFromCents(valorCents)}
                            name="valor"
                            changeFunc={handleValorChange}
                            required
                        />

                        <Hint $ok={valoresBatem}>
                            {valoresBatem ? (
                                <>
                                    <FaCheck size={14} /> Valor confere com o saldo.
                                </>
                            ) : (
                                "O valor informado deve ser igual ao saldo da conta."
                            )}
                        </Hint>

                        {anexosExistentes.length > 0 && (
                            <>
                                <SectionLabel>Anexos já salvos</SectionLabel>
                                <AnexosExistentes>
                                    {anexosExistentes.map((anexo) => (
                                        <AnexoItem key={anexo.id}>
                                            <a
                                                href={anexo.caminho}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {anexo.nome_original || "Arquivo"}
                                            </a>
                                        </AnexoItem>
                                    ))}
                                </AnexosExistentes>
                            </>
                        )}

                        <SectionLabel>Novos anexos</SectionLabel>
                        <MultiFileUploader files={anexos} setFiles={setAnexos} maxFiles={0} maxSizeMB={20} />

                        <BotaoSalvar type="submit" disabled={!valoresBatem || saving}>
                            {saving ? "SALVANDO…" : "SALVAR CONCILIAÇÃO"}
                        </BotaoSalvar>
                    </Formulario>
                )}

                <SectionLabel>Histórico de alterações</SectionLabel>
                <HistoricoLog logs={logs} />
            </Modal>
        </Overlay>
    );
};

export default ModalConciliacaoConta;
